import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import {
  ArrowLeftRight,
  BarChart3,
  Home,
  type LucideIcon,
  MoreHorizontal,
  Plus,
} from "lucide-react"
import { cn } from "@/lib/cn"
import { useUIStore } from "@/store/ui"

// Routes that live behind the "More" tab — kept in sync with the groups
// rendered on the /more page itself.
const MORE_PREFIXES = [
  "/budgets",
  "/upcoming",
  "/subscriptions",
  "/net-worth",
  "/investments",
  "/goals",
  "/debt",
  "/insurance",
  "/calculators",
  "/reports",
  "/heatmap",
  "/settings",
  "/more",
  "/share-target",
]

interface TabLinkProps {
  to: string
  label: string
  icon: LucideIcon
  active: boolean
}

function TabLink({ to, label, icon: Icon, active }: TabLinkProps) {
  return (
    <Link
      to={to}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-0.5 text-label transition-colors",
        active ? "text-accent-green" : "text-text-muted",
      )}
    >
      <Icon className="h-5 w-5" />
      {label}
    </Link>
  )
}

/**
 * Phone-only bottom tab bar (S1). Hidden at md and up, where the Sidebar
 * takes over navigation. "Add" is the primary quick-add entry point — it
 * hands off to the transactions page's existing Add dialog via pendingAction
 * (same hand-off pattern the command palette actions use) rather than
 * duplicating that form here.
 */
export function TabBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()
  const setPendingAction = useUIStore((s) => s.setPendingAction)

  const isHome = pathname === "/"
  const isTransactions = pathname.startsWith("/transactions")
  const isInsights = pathname.startsWith("/analytics")
  const isMore = MORE_PREFIXES.some((prefix) => pathname.startsWith(prefix))

  function quickAdd() {
    setPendingAction("add_transaction")
    navigate({ to: "/transactions" })
  }

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border-default bg-bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <div className="flex h-16 items-stretch">
        <TabLink to="/" label="Home" icon={Home} active={isHome} />
        <TabLink
          to="/transactions"
          label="Transactions"
          icon={ArrowLeftRight}
          active={isTransactions}
        />

        <div className="flex flex-1 items-center justify-center">
          <button
            type="button"
            onClick={quickAdd}
            aria-label="Add transaction"
            className="flex h-12 w-12 -translate-y-3 items-center justify-center rounded-full bg-accent-green text-text-inverse shadow-lg shadow-accent-green/30 transition-transform active:scale-95"
          >
            <Plus className="h-6 w-6" />
          </button>
        </div>

        <TabLink to="/analytics" label="Insights" icon={BarChart3} active={isInsights} />
        <TabLink to="/more" label="More" icon={MoreHorizontal} active={isMore} />
      </div>
    </nav>
  )
}
